import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { ImgSanity } from '@slite/components'
import { SearchIcon } from '@slite/icons'

import { Section } from '../../components/Section'
import { SlitePage } from '../../components/SlitePage'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import type { Data } from './+data'
import { getTemplateCategories, getTemplateColorFromRemote } from './constants'
import { SliteTemplateCard } from './TemplateCard'

export default function TemplatesIndexPage() {
  const intl = useIntl()
  const routes = useRoutes()

  const getInitialCategory = () => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      return params.get('category') || 'all'
    }
    return 'all'
  }

  const [selectedCategory, setSelectedCategory] =
    useState<string>(getInitialCategory)
  const [searchQuery, setSearchQuery] = useState('')

  const { templates } = useData<Data>()
  const templateCategories = getTemplateCategories(intl)

  // Listen to url navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)
      setSelectedCategory(params.get('category') || 'all')
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    setSearchQuery('')

    const currentUrl = new URL(window.location.href)
    if (category === 'all') {
      currentUrl.searchParams.delete('category')
    } else {
      currentUrl.searchParams.set('category', category)
    }
    window.history.pushState({}, '', currentUrl.toString())
  }

  const filteredTemplates = useMemo(() => {
    let results = templates

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      results = results.filter((template) => {
        return (
          template.name.toLowerCase().includes(query) ||
          template.articleDescription.toLowerCase().includes(query)
        )
      })
    } else if (selectedCategory && selectedCategory !== 'all') {
      results = results.filter((template) =>
        template.templateCategories?.includes(selectedCategory)
      )
    }

    return results
  }, [templates, searchQuery, selectedCategory])

  const title = intl.formatMessage({
    id: 'templates.index.title',
    defaultMessage: 'Dive into teamwork with templates',
  })
  const description = intl.formatMessage({
    id: 'templates.index.subtitle',
    defaultMessage:
      'Ready-to-use docs for product specs, company handbooks, meeting notes and more.',
  })

  return (
    <SlitePage
      title={title}
      description={description}
      canonicalUrl={routes.templates.index()}
      background="moon-dust-lighter"
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/891c78f380f64122800a47fb51a51747bbf48296-2400x1260.jpg"
    >
      <Section background="moon-dust-lighter" className="text-align-center">
        <div className="grid-12">
          <div className="grid-span-8-large grid-offset-2-large grid-span-12 flexbox flexbox-column flexbox-align-center">
            <ImgSanity
              src="https://cdn.sanity.io/images/dnsgqii5/production/465d4f068beeca0c40fdb302f8809a5ba884214c-586x356.png"
              alt={intl.formatMessage({
                id: 'templates.index.hero.alt',
                defaultMessage: 'Templates Hero Image',
              })}
              height={120}
              className="margin-bottom-32"
              priority
            />
            <WebsiteText
              variant="display"
              htmlTag="h1"
              className="margin-bottom-16"
              style={{ maxWidth: 650 }}
            >
              {title}
            </WebsiteText>
            <WebsiteText
              variant="body"
              className="font-weight-semibold margin-bottom-32"
              style={{ maxWidth: 500 }}
            >
              {description}
            </WebsiteText>

            <div
              className="position-relative width-full"
              style={{ maxWidth: 360 }}
            >
              <input
                aria-label={intl.formatMessage({
                  defaultMessage: 'Search templates',
                  id: 'templates.index.search.ariaLabel',
                })}
                type="text"
                placeholder={intl.formatMessage({
                  defaultMessage: 'Search',
                  id: 'templates.index.search.placeholder',
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={classNames(
                  'width-full border-small radius-largest padding-vertical-20 font-size-5',
                  searchQuery
                    ? 'background-white border-shade-05'
                    : 'background-transparent border-alpha-shade-40'
                )}
                style={{ paddingLeft: 24, paddingRight: 56 }}
              />
              <SearchIcon
                size={20}
                className="position-absolute shade-30"
                style={{ right: 28, top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <div className="grid-12 grid-gap-40">
          <div className="grid-span-3 display-none-small">
            <div
              className="flexbox flexbox-column flexbox-align-start position-sticky"
              style={{ top: 100 }}
            >
              {templateCategories.map((category) => (
                <a
                  key={category.value}
                  onClick={(e) => {
                    e.preventDefault()
                    handleCategoryChange(category.value)
                  }}
                  className={classNames(
                    'text-align-left font-size-9 font-family-system padding-vertical-8 padding-horizontal-16 margin-bottom-4 radius-largest pointer transition font-weight-semibold',
                    selectedCategory === category.value
                      ? 'background-moon-dust-darker shade-05 shade-05-on-hover'
                      : 'background-transparent shade-10 shade-20-on-hover'
                  )}
                >
                  {category.title}
                </a>
              ))}
            </div>
          </div>
          <div className="grid-span-9 grid-12 grid-align-strech grid-gap-24 grid-gap-12-small grid-span-12-small">
            {filteredTemplates.length === 0 ? (
              <div className="grid-span-12 text-align-center padding-vertical-48">
                <WebsiteText variant="body" className="shade-20">
                  <FormattedMessage
                    defaultMessage="No templates found matching your search."
                    id="templates.index.no-results"
                  />
                </WebsiteText>
              </div>
            ) : (
              filteredTemplates.map((template, index) => {
                const color = getTemplateColorFromRemote(template.color)

                return (
                  <SliteTemplateCard
                    key={`${template._id}-${index}`}
                    slug={template.slug.current}
                    title={template.name}
                    description={template.articleDescription}
                    imageUrl={template.image.url}
                    imageAlt={template.name}
                    color={color}
                  />
                )
              })
            )}
          </div>
        </div>
      </Section>
    </SlitePage>
  )
}
